import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { Provider } from 'react-redux';
import { initDB } from 'react-indexed-db';
import { BrowserRouter } from 'react-router-dom';

import { PersistGate } from 'redux-persist/lib/integration/react';
import { Formio, Components, WebformBuilder } from 'react-formio';

import ErrorBoundary from 'components/ErrorBoundary';
import UserConfirmation from 'components/UserConfirmation';
import { version } from '../package.json';
import { persistor, store } from './redux/store/initStore';
import App from './App';
import IndexedDBconfig from './DBConfig';
import setupAriesComponents from './FormioSetup';
import { AppConfig } from './FormioAppConfig';
import ChatProvider from './components/ChatProvider';
import config from './config';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import '../public/assets/styles/global.css';
import '../public/assets/styles/custom.css';
import '../public/assets/styles/customer.css';
import '../public/assets/styles/customResponsive.css';
import '../public/assets/styles/header.css';
import '../public/assets/styles/reports.css';
import '../public/assets/fonts';
import '../public/assets/styles/tailwind_output.css';
import '../public/assets/js/newrelic'
import { updateReportEntrySynced } from './redux/actions/offlineEnhanced';
Formio.setApiUrl(AppConfig.apiUrl);
Formio.setProjectUrl(AppConfig.projectUrl);
setupAriesComponents(Formio, Components, WebformBuilder);



initDB(IndexedDBconfig); // creating DB for offline signatures

// Service workers registration handling
const { DEV_TOOLS, env } = config;

// Only init Sentry on specific environments

  Sentry.init({
    enabled: true,
    dsn: DEV_TOOLS.SENTRY_DSN,
    environment: DEV_TOOLS.SENTRY_ENV,
    logLevel: 3,
    release: version,
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed with undelivered notifications.'],
  });


const root = createRoot(document.getElementById('root'));
root.render(
  // Wrapper for handling errors
  <ErrorBoundary>
    {/* Wrapper for providing state to app */}
    <Provider store={store}>
      {/* Wrapper for persisting data */}
      <PersistGate persistor={persistor}>
        {/* Wrapper for routing */}
        <BrowserRouter
          getUserConfirmation={(message, callback) =>
            UserConfirmation(message, callback, store.getState().locale.lang)
          }
        >
          {/* Provider for chat */}
          <ChatProvider>
            {/* Main App Component */}
            <App />
          </ChatProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </ErrorBoundary>
);

// Register service worker and handle messages
  window.addEventListener('load', () => {
  const swUrl = `/service-worker.js`;
  
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('./firebase-messaging-sw.js')
      .then((registration) => {
        const persitRoot = JSON.parse(localStorage.getItem("persist:root"))
        const auth = persitRoot?.auth
        if(auth) localStorage.setItem("persist:root", JSON.stringify(auth) )
        console.log('Registration firebase-messaging-sw.js successful, scope is:', registration.scope);
      })
      .catch((err) => {
        console.log('firebase-messaging-sw.js failed, error:', err);
      });
    
    navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Wait for the service worker to be ready and controlling
      return navigator.serviceWorker.ready;
    })
    .then((registration) => {
      
      // Check if we're controlled
      if (!navigator.serviceWorker.controller) {
        console.log('⚠️ Page not controlled by service worker, reloading...');
        window.location.reload();
        return;
      }
      
      console.log('✅ Page controlled by service worker');

      // Handle persist:root safely
      try {
        const persitRootString = localStorage.getItem("persist:root");
        if (persitRootString) {
          const persitRoot = JSON.parse(persitRootString);
          const auth = persitRoot?.auth;
          if (auth) {
            localStorage.setItem("persist:root", JSON.stringify(auth));
          }
        }
      } catch (error) {
        console.error('Error processing persist:root:', error);
      }
      
      // Test communication immediately
      navigator.serviceWorker.controller.postMessage({
        type: 'TEST_CLIENT_CONNECTION'
      });
      
      // Handle registration updates
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (caches) {
              caches.keys().then((names) => {
                for (let name of names) {
                  if(name.includes('workbox-precache-v2')){
                    console.info('Deleting old cache:', name);
                    caches.delete(name);
                  }
                }
              });
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('❌ Service Worker registration failed:', error);
    });
  }

  // Message listeners
  navigator.serviceWorker.addEventListener('message', (event) => {
    console.log('📨 Message received from Service Worker:', event.data);
    
    switch (event.data?.type) {
      case 'GPS_POSITION_UPDATE':
        break;
      case 'GPS_ERROR':
        console.error('❌ GPS Error received:', event.data.error);
        break;
      case 'sync-report-done':
        console.log("BACKGROUND_SYNC__REPORT_SYNCED", event.data.reportId);
        store.dispatch(updateReportEntrySynced(event.data.reportId));
        break;
      case 'get-local-storage': {
        const keys = event.data.keys;
        try {
          console.log("BACKGROUND_SYNC__GET_LOCAL_STORAGE", store.getState());
          const data = {};
          for (const key of keys) {
            data[key] = store.getState()[key];
          }
          event.ports[0].postMessage({ data });
        } catch (error) {
          event.ports[0].postMessage({ error: error.message });
        }
        break;
      }
      case 'SW_REQUEST_GPS_POSITION':
        handleGPSRequestFromServiceWorker(event.data.config);
        break;
        
      case 'SW_STOP_GPS_REQUEST':
        // Handle stopping GPS if needed
        break;
        
      default:
        console.log('Unknown message type:', event.data?.type);
    }
  });
});

// Add this GPS request handler function:
function handleGPSRequestFromServiceWorker(config) {
  console.log('🎯 Main thread: Handling GPS request with config:', config);
  
  if (!navigator.geolocation) {
    console.error('❌ Main thread: Geolocation not supported');
    navigator.serviceWorker.controller?.postMessage({
      type: 'GPS_ERROR_RESPONSE',
      error: 'Geolocation not supported in browser'
    });
    return;
  }
  
  navigator.geolocation.getCurrentPosition(
    (position) => {
      // Send position back to service worker
      navigator.serviceWorker.controller?.postMessage({
        type: 'GPS_POSITION_RESPONSE',
        position: {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed,
            heading: position.coords.heading
          },
          timestamp: position.timestamp
        }
      });
    },
    (error) => {
      console.error('❌ Main thread: GPS error:', error);
      navigator.serviceWorker.controller?.postMessage({
        type: 'GPS_ERROR_RESPONSE',
        error: error.message
      });
    },
    config
  );
}